import { beginNavigation, endNavigation } from '../../shared/navigation';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../app/routes';
import { completeLogin } from '../../app/sessionSlice';
import { useAppDispatch } from '../../app/store';
import type { DestinationOrganization } from '../../core/api/types';
import { DestinationPicker, destinationRoute, savedDestination, type DestinationChoice } from './DestinationPicker';
import './auth.css';

interface OrgSelectLocationState {
  login?: string;
  password?: string;
  from?: string;
  firstName?: string | null;
  organizations?: DestinationOrganization[];
}

/**
 * Destination popup after sign-in (Hub 02 §2, wireframe hub-02-destinations) — replaces the old
 * organization-only "Choose your workspace" list. Kept at `/org-select` so existing links work.
 * Every choice first obtains a PLATFORM session for the chosen organization, then opens
 * Configuration (`/app`), My account (`/me`) or the product console through the one-time launch.
 */
export function OrgSelectPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as OrgSelectLocationState;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Credentials are held only in memory (history state) for this screen.
  if (!state.login || !state.password || !state.organizations?.length) {
    return <Navigate to={ROUTES.login} replace />;
  }

  const open = async ({ organizationId, destination }: DestinationChoice, remember: boolean) => {
    setError(null);
    beginNavigation();
    setBusy(true);
    try {
      await dispatch(completeLogin({ login: state.login!, password: state.password!, organizationId, productCode: 'PLATFORM' })).unwrap();
      if (remember) savedDestination.set(state.login!, { organizationId, code: destination.code });
      navigate(destinationRoute(destination, state.from), { replace: true });
    } catch (caught) {
      endNavigation();
      setError(caught instanceof Error ? caught.message : t('auth.login.failed', 'Sign in failed.'));
      setBusy(false);
    }
  };

  return <div className="ax">
    <DestinationPicker organizations={state.organizations} firstName={state.firstName} busy={busy} error={error} allowRemember
      onOpen={(choice, remember) => void open(choice, remember)}
      onClose={() => navigate(ROUTES.login, { replace: true })} closeLabel={t('destinations.backToSignIn', 'Back to sign in')} />
  </div>;
}
